import type { ERegistrationType } from './person.enum';
import type { EGenderType } from './person.interface';

/**
 * Contracts of the person service for a link registration sent by someone who
 * is ALREADY a person of the gym: finding that person by the CPF, reading it
 * with its phones and addresses, and writing the link's data on it.
 *
 * PascalCase, one to one with the C# view models. Only what this flow reads or
 * writes is declared; the service answers a good deal more.
 */

/**
 * A registration type by its name in lower case, which is how the lookup by
 * document answers it. Taken from the enum, so the two cannot drift apart.
 */
export type TRegistrationTypeName = Lowercase<keyof typeof ERegistrationType>;

/** The person a CPF belongs to, as the lookup by document answers it. */
export interface IRegistrationExistingPerson {
  PersonId: string;
  /**
   * Any of the six types: the lookup looks at nothing but the CPF, so it
   * answers a staff member, a supplier or a helpdesk record as readily as a
   * customer, a visitor or a contact. The number is on the person as read.
   */
  RegistrationType: TRegistrationTypeName;
  DisplayName: string | null;
}

/** Answer of `POST /Person/LookupByDocumentFromExternalCrm`. */
export interface IRegistrationPersonLookup {
  Outcome: 'found' | 'not_found';
  /** `null` when nobody of the gym has the document. */
  Person: IRegistrationExistingPerson | null;
}

/** A phone of the person as read. */
export interface IRegistrationPersonPhone {
  PhoneId: string;
  Type: number | null;
  Number: string | null;
  CountryCode: number | null;
  Confirmed: boolean | null;
  Comment: string | null;
  IsMain: boolean | null;
}

/**
 * An address of the person as read. In the panel `RegionName` is the
 * neighbourhood and `District` is the state, the other way round from the
 * link's `district` and `regionName`.
 */
export interface IRegistrationPersonAddress {
  AddressId: string;
  Type: number | null;
  CountryCode: number | null;
  StreetType: string | null;
  Street: string | null;
  Number: string | null;
  Complement: string | null;
  RegionName: string | null;
  City: string | null;
  District: string | null;
  ZipCode: string | null;
  CityCode: number | null;
  IsFiscal: boolean | null;
}

/**
 * The person as read, with phones and addresses. Both lists are arrays on a
 * read that asked for them, empty when the person has none. Anything else
 * means they were not read, and then neither is ever written.
 */
export interface IRegistrationPerson {
  PersonId: string;
  RegistrationType: ERegistrationType | null;
  DisplayName: string | null;
  FirstName: string | null;
  LastName: string | null;
  /** A date with a time part, as the service answers it: `1994-03-12T00:00:00`. */
  Birthdate: string | null;
  /** `null` is "nobody said". A stored 0 is "Others", a value like the other two. */
  GenderType: EGenderType | null;
  Email: string | null;
  Phones: IRegistrationPersonPhone[] | null;
  Addresses: IRegistrationPersonAddress[] | null;
}

/** A phone inside the update. Without `PhoneId` the service inserts it. */
export type IRegistrationPersonPhonePatch = Partial<{
  [K in keyof IRegistrationPersonPhone]: NonNullable<IRegistrationPersonPhone[K]>;
}>;

/** An address inside the update. Without `AddressId` the service inserts it. */
export type IRegistrationPersonAddressPatch = Partial<{
  [K in keyof IRegistrationPersonAddress]: NonNullable<IRegistrationPersonAddress[K]>;
}>;

/**
 * Body of `PATCH /PersonWithIncludes`, built by omission: a key is there only
 * when it is to be written, and never as `null` nor `''`, which the service
 * may take as "erase".
 *
 * `Phones` and `Addresses` REPLACE the stored list when the key is present: an
 * entry left out is deleted. So either key carries every entry the person has,
 * with its id, or is not sent at all.
 */
export interface IRegistrationPersonPatch {
  PersonId: string;
  FirstName?: string;
  LastName?: string;
  /** `yyyy-MM-dd`, as the link sends it. */
  Birthdate?: string;
  GenderType?: EGenderType;
  Email?: string;
  Phones?: IRegistrationPersonPhonePatch[];
  Addresses?: IRegistrationPersonAddressPatch[];
}

/**
 * How the attendant has the existing person updated with the link's data.
 *
 * - `fill-empty`: only what the person does not have is written.
 * - `overwrite`: the same, and the main mobile and the main address give way to
 *   the link's.
 * - `none`: nothing is written on the person.
 */
export type TRegistrationPersonUpdateChoice = 'fill-empty' | 'overwrite' | 'none';

/** The data of a person the link may bring. */
export type TRegistrationPersonUpdateField =
  | 'firstName'
  | 'lastName'
  | 'birthdate'
  | 'gender'
  | 'email'
  | 'mobile'
  | 'address';

/** The data, in the order the counter compares them. */
export const REGISTRATION_PERSON_UPDATE_FIELDS: readonly TRegistrationPersonUpdateField[] = [
  'firstName',
  'lastName',
  'birthdate',
  'gender',
  'email',
  'mobile',
  'address',
];

/**
 * What becomes of one datum, for one person and one choice.
 *
 * - `fill`: the person has none and the link's is written.
 * - `replace`: the person's gives way to the link's. Only the main mobile and
 *   the main address, and only under `overwrite`.
 * - `keep`: the link brings something the person's record does not have, and
 *   the record stays as it is.
 * - `same`: the record already has what the link brings.
 * - `absent`: the link brings nothing for it.
 * - `refused`: the e-mail alone. The person has none and the link has one, but
 *   it may not be written.
 *
 * Only `fill` and `replace` write.
 */
export type TRegistrationPersonUpdateOutcome =
  | 'fill'
  | 'replace'
  | 'keep'
  | 'same'
  | 'absent'
  | 'refused';

/** What one choice does to one person, told before anything is sent. */
export interface IRegistrationPersonUpdatePlan {
  /** `null` when the choice writes nothing: no request is to be made. */
  body: IRegistrationPersonPatch | null;
  fields: Record<TRegistrationPersonUpdateField, TRegistrationPersonUpdateOutcome>;
}

/**
 * Whether the link's e-mail may become the e-mail of a person who has none.
 * The e-mail is the login, so it is asked before it is written.
 *
 * - `free`: no login of the gym uses it.
 * - `taken`: it is the login of somebody else of the gym.
 * - `unchecked`: the question could not be asked. It is then not written.
 */
export type TRegistrationEmailStatus = 'free' | 'taken' | 'unchecked';
